import { IconFont } from '@byecode/ui'
import type {
    AggregatorDataInputConfig,
    AggregatorFieldSettingConfig,
    AggregatorFilterConfig,
    AggregatorGroupStatisticsConfig,
    AggregatorJoinConfig,
    AggregatorNode,
    AggregatorNodeConfig,
    AggregatorUnionConfig,
    UnionAssociateNodes
} from '@lighthouse/shared'
import { aggregateNodeFieldList, type AggregateResultField, AggregatorNodeType, geyAggregatorNodeIcon } from '@lighthouse/shared'
import { useDebounce } from '@lighthouse/tools'
import type { Edge } from '@xyflow/react'
import { useReactFlow } from '@xyflow/react'
import isEqual from 'fast-deep-equal'
import { find } from 'rambda'
import React, { useMemo } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'

import { useCurrentAppId } from '@/hooks/useApplication'
import { useDataSourceEnvId, useDataSourceList } from '@/hooks/useDataSource'

import { NodeTypeColorMap } from '../AggregatorEditor/constants'
import {
    dataInputResultGenerator,
    fieldSettingConfigGenerator,
    fieldSettingResultGenerator,
    getAggregateFlowByNodes,
    groupStatisticsResultGenerator,
    otherNodeResultGenerator,
    peekPrevNodes
} from '../utils'
import { DataInput } from './DataInput'
import { FieldSetting } from './FieldSetting'
import { Filter } from './Filter'
import type { AggregatorFormSlice } from './formSlice'
import { GroupStatistics } from './GroupStatistics'
import { Join } from './Join'
import { NameInput } from './NameInput'
import { Union } from './Union'

const SCxHeader = styled.div`
    display: flex;
    flex-flow: nowrap row;
    justify-content: flex-start;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
    height: 40px;
`

const SCxIconFontWrapper = styled.div`
    width: 40px;
    height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    background-color: var(--color-gray-100);
`

const SCxContent = styled.div`
    padding: 12px 16px 0 12px;
    flex: 1;
    overflow: hidden;
`

interface AggregatorConfigureProps {
    dsId: string
    node: AggregatorNode
    onChange: (nodes: AggregatorNode[], edges: Edge[], newNode: AggregatorNode) => void
}

export const AggregatorConfigure: React.FC<AggregatorConfigureProps> = ({ dsId, node: propsNode, onChange }) => {
    const { setNodes, getNodes, getEdges } = useReactFlow()
    const appId = useCurrentAppId()
    const envId = useDataSourceEnvId()
    const dataSourceList = useDataSourceList(appId, envId)

    const node = useMemo(() => {
        return {
            ...propsNode,
            selected: true
        }
    }, [propsNode])

    const { prevFieldList, prevNodes } = useMemo(() => {
        const prevNodes = peekPrevNodes(node, getNodes() as AggregatorNode[], getEdges()).nodes
        return {
            prevFieldList: otherNodeResultGenerator(prevNodes),
            prevNodes
        }
    }, [getEdges, getNodes, node])

    const initFormData = useMemo(() => {
        if (node.type === AggregatorNodeType.FILTER) {
            const filter = node.data.config
            if (!filter?.expression || !filter.expression?.where) {
                return {
                    ...filter,
                    expression: {
                        where: 'AND',
                        conditions: filter?.expression?.conditions || []
                    }
                }
            }
            return filter
        }
        return node.data.config
    }, [node.data.config, node.type])

    const methods = useForm<AggregatorFormSlice>({
        mode: 'onChange',
        defaultValues: initFormData as AggregatorFormSlice
    })

    const { control } = methods
    // const onDebounceUpdatePageModules = useMemo(() => debounce(500, onUpdatePageModuleNodes), [onUpdatePageModuleNodes])
    // const fieldList: AggregateResultField[] = []

    const formData = useWatch({ control })

    const debouncedFormData = useDebounce(formData, 500)

    useUpdateEffect(() => {
        if (debouncedFormData && !isEqual(debouncedFormData, node.data.config)) {
            const nodes = getNodes() as AggregatorNode[]
            const edges = getEdges()
            switch (node.type) {
                case AggregatorNodeType.DATA_INPUT: {
                    const config = debouncedFormData as AggregatorDataInputConfig
                    const newFieldList: AggregateResultField[] = dataInputResultGenerator(node.id, config, dataSourceList)

                    const newNode = {
                        ...node,
                        data: {
                            config,
                            result: { fieldList: newFieldList }
                        }
                    }
                    const newNodes = getAggregateFlowByNodes(newNode, nodes, edges)
                    setNodes(newNodes)
                    onChange(newNodes, edges, newNode)
                    aggregateNodeFieldList.emit(node.id, newFieldList)
                    return
                }
                case AggregatorNodeType.JOIN: {
                    const config = debouncedFormData as AggregatorJoinConfig
                    // const newFieldList: AggregateResultField[] = otherNodeResultGenerator(prevNodes)
                    const newNode = {
                        ...node,
                        data: {
                            config,
                            result: { fieldList: prevFieldList }
                        }
                    }
                    aggregateNodeFieldList.emit(node.id, prevFieldList)
                    const newNodes = getAggregateFlowByNodes(newNode, nodes, edges)
                    setNodes(newNodes)
                    onChange(newNodes, edges, newNode)
                    return
                }
                case AggregatorNodeType.UNION: {
                    const config = debouncedFormData as AggregatorUnionConfig
                    const { primaryDsNodeId, unionFields } = config
                    // const primaryDsNode = find(item => item.id === primaryDsNodeId, prevNodes)
                    const { primaryDsNode, unionDsNode } = prevNodes.reduce<UnionAssociateNodes>(
                        (prev, cur) => {
                            if (cur.id === primaryDsNodeId) {
                                prev.primaryDsNode = cur
                                return prev
                            }
                            prev.unionDsNode = cur
                            return prev
                        },
                        {
                            primaryDsNode: undefined,
                            unionDsNode: undefined
                        }
                    )
                    const prevOutputFieldList = primaryDsNode?.data.result?.fieldList || []
                    const prevFieldList = unionFields?.reduce<AggregateResultField[]>((prev, cur) => {
                        const { primaryFieldId } = cur
                        const primaryField = find(item => item.id === primaryFieldId, prevOutputFieldList)
                        if (primaryField) {
                            prev.push(primaryField)
                        }
                        return prev
                    }, [])
                    const newNode = {
                        ...node,
                        data: {
                            config,
                            result: { fieldList: prevFieldList }
                        }
                    }
                    aggregateNodeFieldList.emit(node.id, prevFieldList)
                    const newNodes = getAggregateFlowByNodes(newNode, nodes, edges)
                    setNodes(newNodes)
                    onChange(newNodes, edges, newNode)
                    return
                }
                case AggregatorNodeType.FIELD_SETTING: {
                    const config = debouncedFormData as AggregatorFieldSettingConfig
                    const configFieldList = fieldSettingConfigGenerator(config, prevFieldList)
                    const newConfig = {
                        ...config,
                        fieldList: configFieldList
                    }
                    const fieldList: AggregateResultField[] = fieldSettingResultGenerator(newConfig)
                    const newNode = {
                        ...node,
                        data: {
                            config: newConfig,
                            result: { fieldList }
                        }
                    }
                    aggregateNodeFieldList.emit(node.id, fieldList)
                    const newNodes = getAggregateFlowByNodes(newNode, nodes, edges)
                    setNodes(newNodes)
                    onChange(newNodes, edges, newNode)
                    return
                }
                case AggregatorNodeType.FILTER: {
                    const config = debouncedFormData as AggregatorFilterConfig
                    // const newFieldList: AggregateResultField[] = otherNodeResultGenerator(prevNodes)
                    const newNode = {
                        ...node,
                        data: {
                            config,
                            result: { fieldList: prevFieldList }
                        }
                    }
                    aggregateNodeFieldList.emit(node.id, prevFieldList)
                    const newNodes = getAggregateFlowByNodes(newNode, nodes, edges)
                    setNodes(newNodes)
                    onChange(newNodes, edges, newNode)
                    return
                }
                case AggregatorNodeType.GROUP_STATISTICS: {
                    const config = debouncedFormData as AggregatorGroupStatisticsConfig
                    const newFieldList: AggregateResultField[] = groupStatisticsResultGenerator(config, prevFieldList)
                    const newNode = {
                        ...node,
                        data: {
                            config,
                            result: { fieldList: newFieldList }
                        }
                    }
                    aggregateNodeFieldList.emit(node.id, newFieldList)
                    const newNodes = getAggregateFlowByNodes(newNode, nodes, edges)
                    setNodes(newNodes)
                    onChange(newNodes, edges, newNode)
                    return
                }
                default: {
                    return undefined
                }
            }
        }
    }, [debouncedFormData])

    const content = useMemo(() => {
        switch (node.type) {
            case AggregatorNodeType.DATA_INPUT: {
                return <DataInput dsId={dsId} nodeId={node.id} />
            }
            case AggregatorNodeType.JOIN: {
                return <Join dsId={dsId} nodeId={node.id} nodeList={prevNodes} dataSourceList={dataSourceList} />
            }
            case AggregatorNodeType.FIELD_SETTING: {
                return <FieldSetting dsId={dsId} nodeId={node.id} fieldList={prevFieldList} />
            }
            case AggregatorNodeType.FILTER: {
                return <Filter dsId={dsId} nodeId={node.id} fieldList={prevFieldList} />
            }
            case AggregatorNodeType.GROUP_STATISTICS: {
                return <GroupStatistics dsId={dsId} nodeId={node.id} fieldList={prevFieldList} />
            }
            case AggregatorNodeType.UNION: {
                return <Union dsId={dsId} nodeId={node.id} nodeList={prevNodes} dataSourceList={dataSourceList} />
            }
            default: {
                return null
            }
        }
    }, [node.type, node.id, dsId, prevNodes, dataSourceList, prevFieldList])

    return (
        <FormProvider {...methods}>
            <SCxHeader>
                <SCxIconFontWrapper>
                    {node.type && <IconFont type={geyAggregatorNodeIcon(node)} fill={NodeTypeColorMap[node.type]} size={24} />}
                </SCxIconFontWrapper>
                <Controller
                    name="name"
                    control={control}
                    render={({ field }) => <NameInput value={field.value ?? ''} onChange={field.onChange} />}
                />
            </SCxHeader>
            <SCxContent>{content}</SCxContent>
        </FormProvider>
    )
}
